import { useEffect, useRef } from 'react';

export default function HowItWorks() {
  const stepsRef = useRef([]);

  useEffect(() => {
    const options = {
      root: null, // use viewport
      rootMargin: '0px',
      threshold: 0.2 // 20% of the item visible
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          // Once visible, no need to observe anymore
          observer.unobserve(entry.target);
        }
      });
    }, options);

    // Get current steps and observe them
    const steps = document.querySelectorAll('.step');
    steps.forEach(step => {
      observer.observe(step);
      stepsRef.current.push(step);
    });

    return () => {
      // Cleanup - stop observing all steps
      const currentSteps = stepsRef.current;
      if (currentSteps.length > 0) {
        currentSteps.forEach(step => {
          observer.unobserve(step);
        });
      }
    };
  }, []);

  return (
    <section id="how-it-works" className="how-it-works">
      <h2 className="section-title" style={{  color: 'white', fontSize: '2rem', fontFamily: 'serif', fontWeight: '200', textAlign: 'center', width: '100%', marginBottom: '-1rem' , marginTop: '2rem' }}>How It Works</h2>
      
      <div className="steps-container">
        <div className="step step-right">
          <div className="step-content">
            <div className="step-number">1</div>
            <h3 className="step-title">Capture Your Likeness</h3>
            <p>Upload your photos to our secure platform. Our advanced AI creates a lifelike digital version of you that captures your unique features and expressions.</p>
          </div>
        </div>
        
        <div className="step step-left">
          <div className="step-content">
            <div className="step-number">2</div>
            <h3 className="step-title">Personalize Your Portrait</h3>
            <p>Choose your portrait&apos;s frame style, background, and personality traits. Customize how your portrait responds to different questions and situations.</p>
          </div>
        </div>
        
        <div className="step step-right">
          <div className="step-content">
            <div className="step-number">3</div>
            <h3 className="step-title">Install & Connect</h3>
            <p>Add the WizardPortrait app to your TV. Our simple setup process takes just minutes, with no technical expertise required.</p>
          </div>
        </div>
        
        <div className="step step-left">
          <div className="step-content">
            <div className="step-number">4</div>
            <h3 className="step-title">Experience the Magic</h3>
            <p>Watch as your TV transforms into a magical portrait when not in use. Speak to your portrait and see it respond with realistic expressions and conversations.</p>
          </div>
        </div>
      </div>

      <style jsx>{`
        .how-it-works {
          background: radial-gradient(circle at 50% 50%, rgba(183, 145, 221, 0.47) 0%, rgb(0, 0, 0) 60%);
          background-size: 100% 100%;
          animation: gradientFlow 15s ease infinite;
          padding: 3rem 3rem;
          text-align: center;
          position: relative;
          overflow: hidden;
        }
        
        .how-it-works::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: radial-gradient(circle at 50% 50%, rgba(59, 51, 74, 0.2) 0%, rgba(21, 4, 51, 0) 70%);
          pointer-events: none;
        }
        
        .how-it-works::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-image: 
            radial-gradient(circle at 15% 30%, rgba(9, 49, 52, 0.07) 0%, rgba(5, 217, 232, 0) 20%),
            radial-gradient(circle at 85% 60%, rgba(128, 0, 255, 0.08) 0%, rgba(128, 0, 255, 0) 30%);
          pointer-events: none;
        }
        
        @keyframes gradientFlow {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        
        .section-title {
          font-family: 'Cinzel Decorative', serif;
          font-size: 2rem;
          text-align: left;
          position: relative;
          display: inline-block;
          color: white;
          font-weight: 600;
        }
        
        .section-title::after {
          content: '';
          position: absolute;
          width: 80px;
          height: 4px;
            background: #A16207;
          bottom: -15px;
          left: 50%;
          transform: translateX(-50%);
        }
        
        .steps-container {
          max-width: 1000px;
          margin: 4rem auto 0;
          position: relative;
        }
        
 
        
        .step {
          display: flex;
          margin-bottom: 2rem;
          position: relative;
          opacity: 0;
          transform: translateY(50px);
          transition: 0.2s ease-in-out;
                    color: white;

        }
        
        .step.visible {
          opacity: 1;
          transform: translateY(0);
        }
        
        .step:last-child {
          margin-bottom: 0;
        }
        
        .step-left {
          justify-content: flex-end;
        }
        
        .step-right {
          justify-content: flex-start;
        }
        
        .step-content {
          width: 60%;
          padding: 2rem;
          background: rgba(255, 255, 255, 0.05);
          border-radius: 15px;
          box-shadow: 0 15px 30px rgba(0, 0, 0, 0.2);
          position: relative;
          text-align: left;
          transition: 0.2s ease-in;
        }
        
        .step-content:hover {
          background:rgb(0, 0, 0);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
        }
        
        .step-number {
          position: absolute;
          top: 50%;
          width: 50px;
          height: 50px;
          border-radius: 50%;
          background: #A16207;
          color: black;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-family: 'Cinzel Decorative', serif;
          font-size: 1.5rem;
          transform: translateY(-50%);
          z-index: 2;
        }
        
        .step-left .step-number {
          right: -25px;
        }
        
        .step-right .step-number {
          left: -25px;
        }
        
        .step-title {
          font-size: 1.5rem;
          margin-bottom: 1rem;
          color: var(--accent);
          font-family: serif
        }
        
        @media (max-width: 768px) {
          .step-content {
            width: 99%;
            padding: 1.5rem;
          }
          
          .steps-container::before {
            left: 30px;
          }
          
          .step {
            flex-direction: column;
            margin-left: 10px;
            margin-right: 10px;
          }
          
          .step-left,
          .step-right {
            justify-content: flex-start;
          }
          
          .step-left .step-number,
          .step-right .step-number {
            left: -15px;
          }
          
          .step-title {
            font-size: 1.3rem;
          }
          
          .how-it-works {
            padding: 2rem 1rem;
          }
            .step-number{
            top:1%;
            }
        }
      `}</style>
    </section>
  );
} 